import type { FC, MutableRefObject, ReactNode } from 'react';
import { Children, useRef } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import { Box } from '@packages/shared';
import { generateTransitionStyles } from '@packages/shared/src/utils/generateTransitionStyles/generateTransitionStyles';
import type { TreeNavigationDirection } from './useTreeNavigationPopup';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const transitionDirections = ['left-to-right', 'right-to-left', 'up'] as const;
export type TransitionDirection = (typeof transitionDirections)[number];

export type ResponsiveFlyoutTransitionProps = {
  direction: TreeNavigationDirection;
  transitionKey?: string;
  children: (ref: MutableRefObject<HTMLElement | null>) => ReactNode;
};

const transitionDirectionMap: Record<TreeNavigationDirection, TransitionDirection> = {
  leaf: 'right-to-left',
  root: 'left-to-right',
  lateral: 'up',
};

/**
 * Direction-dependent transition to switch between popup contents by sliding in from left, right, or top, depending on the navigation direction in the nav tree.
 *
 * Parent to child: slide out old items to the left, and new items in from the right
 * Child to parent: slide out old items to the right, and new items in from the left
 * Sibling or different main item: slide out old items to the top and new items in from the top
 */
export const ResponsiveFlyoutTransition: FC<ResponsiveFlyoutTransitionProps> = ({
  direction,
  transitionKey,
  children,
}) => {
  const ref = useRef<HTMLElement>(null);

  return (
    <Box
      className={`header-nav-${transitionDirectionMap[direction]}`}
      // NOTE: transition styles are applied to the parent element with class selectors because SwitchTransition does not support reactive styles based on the direction like we need here
      // TransitionGroup would support it via custom childFactory, but does not support out-in mode
      sx={{
        ...generateTransitionStyles({
          classNames: '&.header-nav-right-to-left .header-nav',
          transition: 'transform 125ms ease-out, opacity 125ms ease-out',
          initial: { transform: 'translateX(1rem)', opacity: 0 },
          animate: { transform: 'translateX(0)', opacity: 1 },
          exit: { transform: 'translateX(-1rem)', opacity: 0 },
        }),
        ...generateTransitionStyles({
          classNames: '&.header-nav-left-to-right .header-nav',
          transition: 'transform 125ms ease-out, opacity 125ms ease-out',
          initial: { transform: 'translateX(-1rem)', opacity: 0 },
          animate: { transform: 'translateX(0)', opacity: 1 },
          exit: { transform: 'translateX(1rem)', opacity: 0 },
        }),
        ...generateTransitionStyles({
          classNames: '&.header-nav-up .header-nav',
          transition: 'transform 125ms ease-out, opacity 125ms ease-out',
          initial: { transform: 'translateY(-0.5rem)', opacity: 0 },
          animate: { transform: 'translateX(0)', opacity: 1 },
        }),

        '@media (prefers-reduced-motion: reduce)': {
          [`.header-nav-enter, .header-nav-enter-active, .header-nav-exit, .header-nav-exit-active`]:
            { transition: 'none !important' },
        },
      }}
    >
      <SwitchTransition mode="out-in">
        <CSSTransition
          appear
          timeout={125}
          key={transitionKey ?? 'nothing'} // fallback key is needed due to https://github.com/reactjs/react-transition-group/issues/866
          classNames="header-nav"
          nodeRef={ref}
        >
          {Children.only(children(ref))}
        </CSSTransition>
      </SwitchTransition>
    </Box>
  );
};
